import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { toast } from "sonner";

const LOGO_URL = "/images/singleseen_logo_transparent.png";

const RegisterPage = () => {
  const { t } = useTranslation();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    confirmPassword: "",
    gender: ""
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    if (!formData.gender) {
      toast.error("Please select your gender");
      return;
    }

    if (!agreedToTerms) {
      toast.error("Please agree to the terms and conditions");
      return;
    }

    setLoading(true);

    try {
      await register({
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        password: formData.password,
        gender: formData.gender
      });
      toast.success("Account created successfully!");
      navigate("/setup");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-pink-50 flex">
      {/* Left Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1617881360412-6f9b11e66267?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxtdXNsaW0lMjBjb3VwbGUlMjBzdW5zZXQlMjBuaWthaHxlbnwwfHx8fDE3NzI0MjgzMjl8MA&ixlib=rb-4.1.0&q=85"
          alt="Muslim couple"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-rose-600/80 to-pink-600/60" />
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-white max-w-md">
            <span className="text-6xl font-light text-rose-200 block mb-6" style={{ fontFamily: "'Amiri', serif" }}>س</span>
            <h2 className="font-serif text-3xl mb-6">{t('register.sideTitle')}</h2>
            <div className="space-y-4">
              {[
                t('register.sideFeatures.verified'),
                t('register.sideFeatures.privacy'),
                t('register.sideFeatures.islamic'),
                t('register.sideFeatures.safe')
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-sm font-light">
                    {index + 1}
                  </span>
                  <span className="text-rose-100">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-8 py-12 overflow-y-auto">
        <div className="max-w-md w-full mx-auto">
          {/* Back Link */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-rose-600 hover:text-rose-800 mb-8"
            data-testid="back-to-home"
          >
            ← {t('nav.backToHome')}
          </Link>

          {/* Logo */}
          <div className="flex items-center gap-2 mb-8">
            <img src={LOGO_URL} alt="SingleSeen" className="h-32 w-auto" />
          </div>

          <h1 className="font-serif text-3xl text-rose-900 mb-2">{t('register.title')}</h1>
          <p className="text-rose-600 mb-8">{t('register.subtitle')}</p>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name" className="text-rose-700">{t('common.firstName')}</Label>
                <Input
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  placeholder={t('register.firstNamePlaceholder')}
                  className="bg-white border-rose-200 focus:border-rose-500 h-12"
                  required
                  data-testid="register-firstname"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="last_name" className="text-rose-700">{t('common.lastName')}</Label>
                <Input
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  placeholder={t('register.lastNamePlaceholder')}
                  className="bg-white border-rose-200 focus:border-rose-500 h-12"
                  required
                  data-testid="register-lastname"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-rose-700">{t('login.emailLabel')}</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your@email.com"
                className="bg-white border-rose-200 focus:border-rose-500 h-12"
                required
                data-testid="register-email"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-rose-700">{t('register.genderLabel')}</Label>
              <RadioGroup 
                value={formData.gender} 
                onValueChange={(value) => setFormData({ ...formData, gender: value })}
                className="flex gap-4"
                data-testid="register-gender"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="male" id="male" className="border-rose-500 text-rose-500" />
                  <Label htmlFor="male" className="text-rose-700 cursor-pointer">{t('common.male')}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="female" id="female" className="border-rose-500 text-rose-500" />
                  <Label htmlFor="female" className="text-rose-700 cursor-pointer">{t('common.female')}</Label>
                </div>
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-rose-700">{t('common.password')}</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Create a password"
                  className="pr-16 bg-white border-rose-200 focus:border-rose-500 h-12"
                  required
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-rose-400 hover:text-rose-600 text-sm font-medium"
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-rose-700">{t('common.confirmPassword')}</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type={showPassword ? "text" : "password"}
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm your password"
                className="bg-white border-rose-200 focus:border-rose-500 h-12"
                required
                data-testid="register-confirm-password"
              />
            </div>

            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id="terms"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                className="mt-1 rounded border-rose-300 text-rose-500 focus:ring-rose-500"
                data-testid="register-terms"
              />
              <Label htmlFor="terms" className="text-sm text-rose-600 cursor-pointer">
                I agree to the <a href="#" className="text-rose-700 hover:underline">Terms of Service</a> and{" "}
                <a href="#" className="text-rose-700 hover:underline">Privacy Policy</a>
              </Label>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-rose-500 to-pink-500 text-white hover:from-rose-600 hover:to-pink-600 rounded-full h-12 text-lg font-medium shadow-lg shadow-rose-500/30"
              data-testid="register-submit"
            >
              {loading ? t('common.creatingAccount') : t('common.createAccount')}
            </Button>
          </form>

          <p className="text-center text-rose-600 mt-8">
            {t('register.alreadyAccount')}{" "}
            <Link to="/login" className="text-rose-700 font-medium hover:underline" data-testid="login-link">
              {t('common.signIn')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
